import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  PageLayout,
  useTrackingVisitor,
} from '@xing-com/crate-entity-pages-common';
import { Appcues } from '@xing-com/crate-entity-pages-entity-pages-shared-appcues';
import { PageError } from '@xing-com/crate-entity-pages-entity-pages-shared-components-page-error';
import { PageContextProvider } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { ErrorMessage } from '@xing-com/crate-entity-pages-entity-pages-shared-context-error-context';
import { ModulesContextProvider } from '@xing-com/crate-entity-pages-entity-pages-shared-context-modules-context';
import { EditorUrlParameter } from '@xing-com/crate-entity-pages-entity-pages-shared-policies-editor-url-parameter';
import { Header } from '@xing-com/crate-entity-pages-header';
import { NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';

import { MainPageLayout } from './app-layout';
import { MetaDataContainer } from './components/main-page-metadata/main-page-metadata';

type AppProps = {
  pageSlug: string;
};
const AppContexts: FC<PropsWithChildren<AppProps>> = ({
  children,
  pageSlug,
}) => {
  const { trackVisitor } = useTrackingVisitor({ pageSlug });

  useEffect(() => {
    trackVisitor();
  }, []);

  return (
    <PageContextProvider pageSlug={pageSlug}>
      <EditorUrlParameter>
        <ModulesContextProvider>
          {/* TODO: Appcues should be a hook as well */}
          <Appcues>{children}</Appcues>
        </ModulesContextProvider>
      </EditorUrlParameter>
    </PageContextProvider>
  );
};

const AppFallback: FC = () => {
  return (
    <>
      <MetaDataContainer setDefault={true} />
      <Header.Skeleton />
    </>
  );
};

export const App: FC<AppProps> = ({ pageSlug }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageLayout>
      <ErrorMessage />
      <ErrorBoundary onError={() => <PageError />}>
        <NoSSRLoggedInSuspense fallback={<AppFallback />}>
          <AppContexts pageSlug={pageSlug}>
            <MainPageLayout />
          </AppContexts>
        </NoSSRLoggedInSuspense>
      </ErrorBoundary>
    </PageLayout>
  );
};
